import { Directive, ElementRef, HostListener, Input, NgZone, OnDestroy } from '@angular/core';
import type { SimpleChanges } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
@Directive({
  selector: '[showTooltipOnOverflow]',
  standalone: true
})
export class ShowtooltipDirective implements OnDestroy {
  @Input() tooltipDelayMs: number | string = 0;

  private resizeObserver?: ResizeObserver;
  private textEl!: HTMLElement;
  private lastMessage = '';

  constructor(private el: ElementRef<HTMLElement>,
    private tooltip: MatTooltip,
    private ngZone: NgZone
  ) {
    this.textEl = this.resolveTextElement(this.el.nativeElement);
    this.initResizeObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltipDelayMs']) {
      const parsed = Number(this.tooltipDelayMs);

      if (Number.isFinite(parsed) && parsed >= 0) {
        this.tooltip.showDelay = parsed;
      }
    }
  }

  /** Initialize ResizeObserver outside Angular */
  private initResizeObserver(): void {
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.checkOverflow());
      this.resizeObserver.observe(this.el.nativeElement);
    });
  }

  /** Overflow check */
  private checkOverflow(): void {
    const hasOverflow =
      this.textEl.scrollWidth > this.textEl.clientWidth ||
      this.textEl.scrollHeight > this.textEl.clientHeight;

    const message = hasOverflow ? this.textEl.innerText : '';

    if (message !== this.lastMessage) {
      this.lastMessage = message;
      this.ngZone.run(() => (this.tooltip.message = message));
    }
  }

  /** Resolve text node only once */
  private resolveTextElement(root: HTMLElement): HTMLElement {
    for (const child of Array.from(root.children)) {
      if (child.textContent?.trim()) {
        return child as HTMLElement;
      }
    }
    return root;
  }

  /** Re-check overflow on hover to keep tooltip message up to date */
  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.checkOverflow();
  }

  /** Clear tooltip message on mouse leave */
  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.lastMessage) {
      this.lastMessage = '';
      this.tooltip.message = '';
    }
  }

  /** Disconnect observer and release references */
  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined as any;
    this.textEl = undefined as any;
  }
}
